import { useEffect, useRef, type KeyboardEvent } from 'react';

interface EditableTextProps {
  value: string;
  placeholder?: string;
  onCommit: (next: string) => void;
  className?: string;
  multiline?: boolean;
  ariaLabel?: string;
}

export const EditableText = ({
  value,
  placeholder,
  onCommit,
  className,
  multiline = false,
  ariaLabel,
}: EditableTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);

  // Sync external value into the DOM only when it changes outside of editing.
  useEffect(() => {
    const node = ref.current;
    if (node && document.activeElement !== node && node.textContent !== value) {
      node.textContent = value;
    }
  }, [value]);

  const handleBlur = () => {
    const next = ref.current?.textContent?.trim() ?? '';
    if (next !== value) onCommit(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' && !multiline) {
      event.preventDefault();
      ref.current?.blur();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (ref.current) ref.current.textContent = value;
      ref.current?.blur();
    }
  };

  return (
    <span
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      data-placeholder={placeholder}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`-mx-1 w-fit rounded px-1 outline-none empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)] focus:ring-1 focus:ring-teal-400 ${className ?? ''}`}
    >
      {value}
    </span>
  );
};
